import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import { Component, inject, Injector, signal } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxAmountForm } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxToastMessage,
  OnyxToastService,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { ValidationHelper } from '../../../../../../../../common/helpers/validation.helper';
import { PreferencesService } from '../../../../../../../../common/services/preferences.service';
import type { Order } from '../../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../../common/services/orders.service';
import type {
  OrderModalPaymentDto,
  OrderPaymentHistoryItemData,
} from '../../order-modal-payment-form-container/order-modal-payment-form-container.component';
import { OrderModalPaymentFormComponent } from '../../order-modal-payment-form/order-modal-payment-form.component';

export interface OrderPaymentEditModalData {
  order: Order;
  data: OrderPaymentHistoryItemData;
}

@Component({
  selector: 'app-order-modal-payment-edit',
  imports: [
    OnyxModalComponent,
    OnyxButtonComponent,
    TranslatePipe,
    OrderModalPaymentFormComponent,
  ],
  templateUrl: './order-modal-payment-edit.component.html',
  styleUrl: './order-modal-payment-edit.component.scss',
})
export class OrderModalPaymentEditComponent implements OnInit {
  protected readonly data = inject<OrderPaymentEditModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly ordersService = inject(OrdersService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'orders.orderModal';

  protected readonly OnyxFormMode = OnyxFormMode;

  protected close$ = new Subject<void>();
  protected loading = signal(false);
  protected form = OrderModalPaymentFormComponent.buildForm(
    this.fb,
    this.preferencesService,
  );

  public ngOnInit(): void {
    const { date, net, vat } = this.data.data;

    this.form.patchValue({
      date,
      net: { ...net, value: net?.value ?? null },
      vat: { ...vat, value: vat?.value ?? null },
    });
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();
    const dto: OrderModalPaymentDto = {
      ...form,
      net: form.net.value ? (form.net as OnyxAmountForm) : null,
      vat: form.vat.value ? (form.vat as OnyxAmountForm) : null,
    };

    if (!dto.net && !dto.vat) {
      this.toastService.showError({
        message: `${this.I18N}.toasts.requiredOneField`,
      });
      return;
    }

    this.loading.set(true);
    this.ordersService
      .editPayment(this.data.order.uuid, dto, this.data.data.uuid)
      .subscribe(() => {
        this.toastService.showCustom(OnyxToastMessage.UPDATED_DATA);
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  protected deletePayment() {
    this.ordersService
      .deletePayment(this.data.order.uuid, this.data.data.uuid)
      .subscribe(() => {
        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.paymentDeleted`,
          keepOnNavigation: true,
        });
        this.close$.next();
      });
  }
}
